import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, KeyRound, Smartphone, Copy, AlertTriangle } from "lucide-react";

export default function Security() {
  const { refresh } = useAuth();
  const [user, setUser] = useState(null);

  // 2FA setup state
  const [setupData, setSetupData] = useState(null); // { secret, qr_png_base64, provisioning_uri }
  const [setupCode, setSetupCode] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);

  // 2FA disable state
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableBusy, setDisableBusy] = useState(false);

  // Change password state
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const loadMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.error("Security: failed to load /auth/me", err?.message || err);
    }
  };

  useEffect(() => {
    loadMe();
  }, []);

  const startSetup = async () => {
    setSetupBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore setup");
    } finally {
      setSetupBusy(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    if (setupCode.length !== 6) return;
    setSetupBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code: setupCode });
      toast.success("Autenticazione a due fattori attivata!");
      setSetupData(null);
      setSetupCode("");
      await loadMe();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido");
    } finally {
      setSetupBusy(false);
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    setDisableBusy(true);
    try {
      await api.post("/auth/2fa/disable", {
        password: disablePw,
        code: disableCode || undefined,
      });
      toast.success("2FA disattivato");
      setDisablePw("");
      setDisableCode("");
      await loadMe();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setDisableBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew !== pwNew2) {
      toast.error("Le nuove password non coincidono");
      return;
    }
    if (pwNew.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwOld,
        new_password: pwNew,
      });
      toast.success("Password cambiata. Riusala al prossimo accesso.");
      setPwOld(""); setPwNew(""); setPwNew2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setPwBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiato negli appunti");
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <div className="label-mono">Account</div>
        <h1 className="font-display text-4xl mt-2 font-light">Sicurezza</h1>
        <p className="text-sm text-[#5C5B5A] mt-2">
          Gestisci accesso, password e autenticazione a due fattori.
        </p>
      </div>

      {/* 2FA STATUS */}
      <section className="border border-[#E5E4E0] bg-white p-6">
        <div className="flex items-center gap-3 border-b border-[#E5E4E0] pb-4">
          {user?.totp_enabled ? (
            <ShieldCheck size={22} className="text-[#2C4C3B]" />
          ) : (
            <ShieldOff size={22} className="text-[#C85A32]" />
          )}
          <div>
            <h3 className="font-display text-2xl font-light">Verifica a due fattori</h3>
            <p className="text-xs text-[#5C5B5A] font-mono mt-1">
              {user?.totp_enabled ? "ATTIVA · Account protetto" : "NON ATTIVA · Consigliata"}
            </p>
          </div>
        </div>

        {!user?.totp_enabled && !setupData && (
          <div className="mt-5 space-y-4">
            <p className="text-sm text-[#1C1B1A]">
              Aggiunge un livello in più al login: dopo la password ti verrà chiesto un codice di 6 cifre
              generato dall&apos;app autenticatore sul tuo telefono (Google Authenticator, Authy, 1Password).
              Se qualcuno ruba la tua password, senza il telefono non può entrare.
            </p>
            <Button
              data-testid="security-setup-2fa-button"
              onClick={startSetup}
              disabled={setupBusy}
              className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
            >
              <Smartphone size={14} className="mr-2" /> Attiva 2FA
            </Button>
          </div>
        )}

        {!user?.totp_enabled && setupData && (
          <div className="mt-5 space-y-5">
            <ol className="text-sm text-[#1C1B1A] space-y-2 list-decimal list-inside">
              <li>Installa <strong>Google Authenticator</strong> (iOS / Android) — gratis</li>
              <li>Apri l&apos;app → tocca <strong>+</strong> → <strong>Scansiona un codice QR</strong></li>
              <li>Inquadra il QR qui sotto</li>
              <li>L&apos;app mostrerà un codice di 6 cifre che cambia ogni 30 secondi: inseriscilo qui sotto</li>
            </ol>
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div className="border border-[#E5E4E0] p-4 bg-white">
                <img
                  src={`data:image/png;base64,${setupData.qr_png_base64}`}
                  alt="QR code 2FA"
                  data-testid="security-2fa-qr"
                  className="w-full max-w-[240px] mx-auto"
                />
                <div className="mt-3 text-center">
                  <div className="label-mono text-[10px]">o inserisci manualmente</div>
                  <button
                    type="button"
                    onClick={() => copy(setupData.secret)}
                    data-testid="security-2fa-secret"
                    className="font-mono text-xs text-[#2C4C3B] mt-1 inline-flex items-center gap-1 hover:underline break-all"
                  >
                    {setupData.secret} <Copy size={11} />
                  </button>
                </div>
              </div>
              <form onSubmit={confirmSetup} className="space-y-4">
                <div>
                  <Label className="label-mono">Codice dall&apos;app (6 cifre)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    autoFocus
                    data-testid="security-2fa-verify-input"
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="mt-2 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono text-2xl text-center tracking-[0.4em] py-4"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={setupBusy || setupCode.length !== 6}
                    data-testid="security-2fa-confirm"
                    className="flex-1 bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
                  >
                    Conferma & attiva
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setSetupData(null); setSetupCode(""); }}
                    className="border-[#E5E4E0] text-[#5C5B5A] hover:bg-[#F0EFEA] font-mono uppercase text-xs tracking-[0.2em]"
                  >
                    Annulla
                  </Button>
                </div>
                <div className="text-xs text-[#C85A32] flex items-start gap-1.5">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  <span>Salva la chiave segreta in un posto sicuro (es. password manager). Se perdi il telefono ti servirà per ripristinare.</span>
                </div>
              </form>
            </div>
          </div>
        )}

        {user?.totp_enabled && (
          <form onSubmit={disable2FA} className="mt-5 space-y-4">
            <p className="text-sm text-[#1C1B1A]">
              Per disattivare la 2FA conferma la password attuale (e opzionalmente un codice 2FA valido).
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="label-mono">Password attuale</Label>
                <Input
                  type="password"
                  required
                  data-testid="security-disable-password"
                  value={disablePw}
                  onChange={(e) => setDisablePw(e.target.value)}
                  className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
                />
              </div>
              <div>
                <Label className="label-mono">Codice 2FA (opzionale)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  data-testid="security-disable-code"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono"
                  placeholder="123456"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={disableBusy}
              data-testid="security-disable-2fa-button"
              variant="outline"
              className="border-[#C85A32] text-[#C85A32] hover:bg-[#FDF1EB] font-mono uppercase text-xs tracking-[0.2em]"
            >
              Disattiva 2FA
            </Button>
          </form>
        )}
      </section>

      {/* CHANGE PASSWORD */}
      <section className="border border-[#E5E4E0] bg-white p-6">
        <div className="flex items-center gap-3 border-b border-[#E5E4E0] pb-4">
          <KeyRound size={22} className="text-[#2C4C3B]" />
          <h3 className="font-display text-2xl font-light">Cambia password</h3>
        </div>
        <form onSubmit={changePassword} className="mt-5 grid md:grid-cols-3 gap-4">
          <div>
            <Label className="label-mono">Password attuale</Label>
            <Input
              type="password"
              required
              data-testid="security-pw-current"
              value={pwOld}
              onChange={(e) => setPwOld(e.target.value)}
              className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
            />
          </div>
          <div>
            <Label className="label-mono">Nuova password (min 8)</Label>
            <Input
              type="password"
              required
              minLength={8}
              data-testid="security-pw-new"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
            />
          </div>
          <div>
            <Label className="label-mono">Conferma nuova</Label>
            <Input
              type="password"
              required
              minLength={8}
              data-testid="security-pw-new2"
              value={pwNew2}
              onChange={(e) => setPwNew2(e.target.value)}
              className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
            />
          </div>
          <div className="md:col-span-3">
            <Button
              type="submit"
              disabled={pwBusy}
              data-testid="security-pw-submit"
              className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
            >
              Cambia password
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
